import React, { useCallback, type ReactElement } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { markdownModeMessages as i18n } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import TextNormalIcon from '@atlaskit/icon-lab/core/text-normal';
import ViewSplitIcon from '@atlaskit/icon-lab/core/view-split';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import { Inline } from '@atlaskit/primitives/compiled';

import type { MarkdownModePlugin, MarkdownModeView } from '../markdownModePluginType';

// Avoid creating a new object on every render
const tooltipProps = {
	position: 'top' as const,
};

// ---------------------------------------------------------------------------
// Base (presentational) component — shared by both the plugin toolbar toggle
// and the Confluence floating toggle.
// ---------------------------------------------------------------------------

export type MarkdownModeViewToggleBaseProps = {
	/** Called when the user clicks a view button. */
	onViewChange: (nextView: MarkdownModeView) => void;
	/** Current active view. */
	view: MarkdownModeView;
};

/**
 * Pure presentational 3-button view-mode toggle (source / wysiwyg / preview).
 *
 * This component is stateless — the consumer is responsible for providing the
 * current `view` and handling `onViewChange`. This allows it to be shared
 * between the editor plugin (ProseMirror state) and Confluence (sweet-state).
 */
export const MarkdownModeViewToggleBase = ({
	view,
	onViewChange,
}: MarkdownModeViewToggleBaseProps): ReactElement => {
	const intl = useIntl();

	const handleClick = useCallback(
		(nextView: MarkdownModeView) => {
			if (nextView === view) {
				return;
			}
			onViewChange(nextView);
		},
		[view, onViewChange],
	);

	// Stable per-view click handlers so the IconButtons receive a referentially
	// equal `onClick` prop across renders (avoids breaking memoisation in the
	// IconButton subtree). `handleClick` itself is already memoised on
	// (view, onViewChange).
	const handleSourceClick = useCallback(() => handleClick('source'), [handleClick]);
	const handleWysiwygClick = useCallback(() => handleClick('wysiwyg'), [handleClick]);
	const handlePreviewClick = useCallback(() => handleClick('preview'), [handleClick]);

	const sourceLabel = intl.formatMessage(i18n.source);
	const wysiwygLabel = intl.formatMessage(i18n.wysiwyg);
	const previewLabel = intl.formatMessage(i18n.preview);
	const groupLabel = intl.formatMessage(i18n.groupLabel);

	return (
		<div role="group" aria-label={groupLabel} data-testid="markdown-mode-view-toggle">
			<Inline space="space.050">
				<IconButton
					icon={AngleBracketsIcon}
					label={sourceLabel}
					appearance="subtle"
					isSelected={view === 'source'}
					onClick={handleSourceClick}
					testId="markdown-mode-view-toggle--source"
					aria-pressed={view === 'source'}
					spacing="compact"
					isTooltipDisabled={false}
					tooltip={tooltipProps}
				/>
				<IconButton
					icon={TextNormalIcon}
					label={wysiwygLabel}
					appearance="subtle"
					isSelected={view === 'wysiwyg'}
					onClick={handleWysiwygClick}
					testId="markdown-mode-view-toggle--wysiwyg"
					aria-pressed={view === 'wysiwyg'}
					spacing="compact"
					isTooltipDisabled={false}
					tooltip={tooltipProps}
				/>
				<IconButton
					icon={ViewSplitIcon}
					label={previewLabel}
					appearance="subtle"
					isSelected={view === 'preview'}
					onClick={handlePreviewClick}
					testId="markdown-mode-view-toggle--preview"
					aria-pressed={view === 'preview'}
					spacing="compact"
					isDisabled
					isTooltipDisabled={false}
					tooltip={tooltipProps}
				/>
			</Inline>
		</div>
	);
};

// ---------------------------------------------------------------------------
// Plugin-connected wrapper — reads and writes ProseMirror plugin state.
// ---------------------------------------------------------------------------

export type MarkdownModeViewToggleProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
};

export const MarkdownModeViewToggle = ({
	api,
}: MarkdownModeViewToggleProps): ReactElement | null => {
	const { view, isMarkdownMode } = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => ({
			view: states.markdownModeState?.view ?? ('wysiwyg' as const),
			isMarkdownMode: states.markdownModeState?.isMarkdownMode ?? false,
		}),
	);

	const handleViewChange = useCallback(
		(nextView: MarkdownModeView) => {
			api?.markdownMode?.actions.setView(nextView);
		},
		[api],
	);

	if (!isMarkdownMode) {
		return null;
	}

	return <MarkdownModeViewToggleBase view={view} onViewChange={handleViewChange} />;
};
